/* eslint-disable operator-linebreak */
import React, { useCallback, useState, useEffect } from 'react';
import { FormSpy } from 'react-final-form';
import { FormState, FormApi } from 'final-form';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { CircularProgress, makeStyles } from '@material-ui/core';
import noop from 'utils/noop';
import NavigationTitle, { LayoutType } from 'ui/NavigationTitle/NavigationTitle';
import { ITransactionField } from 'ui/structure/TransactionCard';
import { getTransactionsArray } from 'utils/mapping/transactionCardMapper';
import routePaths from 'routes/routePaths';
import { OTPConfirmationModal, StatusModal, WarningModal } from 'components/modals';
import Form from 'ui/Form/Form';
import { getFileSize } from 'utils/formatters';
import Button, { Themes as ButtonThemes, Sizes as ButtonSizes } from 'ui/Button/Button';
import { AxiosStatus } from 'api/axiosConfig';
import FieldsLeveler from 'ui/structure/FieldsLeveler/FieldsLeveler';
import SubmitLeveler from 'ui/structure/SubmitLeveler/SubmitLeveler';
import { NETWORK_REQUIRED } from 'utils/constants';

import validateOtpPin, { handlePinError } from 'utils/otpValidation';
import Table from 'ui/Table/Table';
import { requiredOnly, validationCallback } from 'utils/validation';
import DashboardCard from 'ui/DashboardCard/DashboardCard';
import { changeFieldValue, setFieldTouched } from 'utils/formMutators';
import { IAccount } from 'store/accounts.model';
import api from 'api/api';
import { IApiResponse } from 'utils/models';
import TablePagination from 'ui/TableInputs/TablePagination/TablePagination';
import { IOTPError } from 'components/authModules/LogIn/login.model';
import {
  IFormValues,
  IExtraFields,
  FAB_BANK,
  FormType,
  bulkDataConfig,
  bulkSortArray,
  IBulkUploadData,
  IBulkDataList,
  ITransferResponse,
} from './BulkFileUpload.model';
import {
  renderTransferFieldsFrom,
  renderBulkTransferUpload,
  renderScheduleControl,
  renderScheduleForm,
} from './BulkFileUploadAccountsFields';
import columns from './columns';

// eslint-disable-next-line css-modules/no-unused-class
import styles from './BulkFileUpload.module.css';
import trash from 'assets/icons/trash.png';
import excel from 'assets/icons/excel.png';
import download from 'assets/img/download.png';

const schema = yup.object().shape({
  sourceAccount: requiredOnly('Source Account'),
  files: requiredOnly('File upload'),
  scheduleTransfer: yup.boolean(),
  frequency: yup.string().when('scheduleTransfer', {
    is: true,
    then: requiredOnly('Frequency'),
  }),
  startDate: yup.string().test('startDate', 'Special Date is required', (value, context) => {
    const FABValues: IFormValues = context.parent as IFormValues;
    if (FABValues?.frequency === 'specialDate') {
      return !!value;
    }
    return true;
  }),
});

interface IProps {
  accounts: IAccount[];
  bulkTransfersState: 'adding' | 'confirmation' | 'report';
  // eslint-disable-next-line react/no-unused-prop-types
  openAddBulkTransfersForm?: () => void;
  showMainForm: boolean;
  handleChangeForm: () => void;
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    columnGap: '24px',
    margin: 0,
    paddingTop: 16,
  },
  label: {
    fontFamily: 'var(--ff-etelka-text)',
    fontSize: 14,
    lineHeight: '24px',
    '&.Mui-disabled': {
      color: '#000',
    },
  },
});

const BulkFileUpload = ({ accounts, bulkTransfersState, showMainForm, handleChangeForm }: IProps) => {
  const [submitStatus, setSubmitStatus] = useState<AxiosStatus>(AxiosStatus.DEFAULT);
  const submitting = submitStatus === AxiosStatus.PENDING;
  const [OTPError, setOTPError] = useState<IOTPError | null>(null);
  const [isConfirmationOpen, setConfirmationOpen] = useState(false);
  const [previewData, setPreviewData] = useState<ITransactionField[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [successMsg, setSuccessMsg] = useState<string>('');
  const emptyMessage = 'Payments will be displayed here.';

  // Extras
  const INITIAL_EXTRAS: IExtraFields = {
    destinationBankName: '',
    destinationBankCode: '',
    destinationAccountName: '',
    destinationIsExistingBen: false,
  };
  const [extras, setExtras] = useState<IExtraFields>(INITIAL_EXTRAS);
  const updateExtras = useCallback(
    (update: Partial<IExtraFields>) => setExtras((current) => ({ ...current, ...current, ...update })),
    [],
  );

  const [simpleWarning, setSimpleWarning] = useState<string>();
  const [toggleSchedule, setToggleSchedule] = useState<boolean | null>(null);
  const showStatusModal = submitStatus === AxiosStatus.SUCCESS || submitStatus === AxiosStatus.FAILURE;

  const [tableData, setTableData] = useState<IBulkDataList[]>([]);
  const [mainData, setMainData] = useState<IBulkDataList[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRows, setTotalRows] = useState(0);
  const [disableBulkTransfer, setDisableMakeTransfer] = useState<boolean>(false);

  let mainForm!: FormApi<IFormValues, Partial<IFormValues>>;
  const validate = useCallback((values: IFormValues) => validationCallback<unknown>(schema)(values), []);

  const onChangeForm = useCallback(
    ({ values, dirty }: FormState<IFormValues>) => {
      if (dirty) {
        handleChangeForm();
      }
      const { destinationAccountNo } = values || {};
      const destAccount = accounts.find((a) => a.accountNumber === destinationAccountNo);
      if (destAccount) {
        updateExtras({
          destinationAccountName: destAccount.accountName,
          destinationBankName: FAB_BANK.name,
          destinationBankCode: FAB_BANK.code,
          destinationIsExistingBen: false,
          beneficiaryId: undefined,
        });
      } else {
        console.error('DID NOT FIND OWN ACCOUNT');
        // todo: if destAccount not found, prompt
      }
    },
    [accounts, updateExtras, handleChangeForm],
  );

  useEffect(() => {
    if (toggleSchedule == null) {
      return;
    }

    mainForm.change('scheduleTransfer', toggleSchedule);
    if (!toggleSchedule) {
      mainForm.change('frequency', '');
      mainForm.change('startDate', '');
    }
  }, [toggleSchedule]);

  const history = useHistory();
  const handleOnSuccess = () => {
    history.push(routePaths.dashboard);
  };

  const closeSimpleWarning = useCallback(() => setSimpleWarning(undefined), []);

  const openConfirmation = () => setConfirmationOpen(true);
  const closeConfirmation = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    } else {
      setConfirmationOpen(false);
    }
  };

  const onCloseStatusModal = () => {
    if (submitStatus === AxiosStatus.SUCCESS) {
      handleOnSuccess();
    }
    setSubmitStatus(AxiosStatus.DEFAULT);
  };

  const getPrincipalAmount = () => {
    const transferList = mainData;

    if (transferList.length > 0) {
      const result = transferList.map((item) => Number(item.transactionAmount)).reduce((a, b) => a + b, 0);
      return result;
    }
    return 0;
  };

  const onRejectedSubmit = useCallback((error: IApiResponse) => {
    console.error(error || NETWORK_REQUIRED);
    setErrorMsg(error.description || NETWORK_REQUIRED);
    setSubmitStatus(AxiosStatus.FAILURE);
    closeConfirmation();
  }, []);

  const getTotalCharge = () => {
    if (mainData.length > 0) {
      const result = mainData.map((item) => Number(item.charge)).reduce((a, b) => a + b, 0);
      return result || 0;
    }
    return 0;
  };

  const BulkTransfersConfirmation = () => {
    const { sourceAccount } = mainForm.getState().values;

    const readyValues = {
      sourceAccount,
      numberOfTransactions: mainData.length,
      totalTransactionAmount: getPrincipalAmount(),
      transactionCharge: getTotalCharge() || 0,
      totalAmount: getPrincipalAmount() + getTotalCharge(),
    };

    const account = accounts.find((a) => a.accountNumber === sourceAccount);
    const totalAmount = getPrincipalAmount() + getTotalCharge();
    if (Number(account?.availableBalance) < Number(totalAmount)) {
      setSubmitStatus(AxiosStatus.FAILURE);
      setErrorMsg('Transfer failed.\nInsufficient funds to complete the transaction.');
      return;
    }
    // setSubmitStatus(AxiosStatus.PENDING);
    setPreviewData(getTransactionsArray(readyValues, bulkDataConfig, undefined, bulkSortArray));
    setSubmitStatus(AxiosStatus.PENDING);
    api
      .post('/customer/otp/generate')
      .then(() => {
        setSubmitStatus(AxiosStatus.DEFAULT);
        openConfirmation();
      })
      .catch((data) => {
        onRejectedSubmit(data);
      });
  };

  const onResend = () => api.post('/customer/otp/generate');
  const onRetryConfirm = () => setSubmitStatus(AxiosStatus.DEFAULT);

  const handleAddBulkTransfer = useCallback(
    (values: IFormValues) => {
      setSubmitStatus(AxiosStatus.PENDING);
      const { sourceAccount } = values;
      const fromAccount = accounts.find((a) => a.accountNumber === sourceAccount);
      if (!fromAccount) {
        console.error('Invalid source account selected');
        // TODO: show prompt
      }
      const requestBody = {
        sourceAccount: fromAccount?.accountNumber,
        file: values.files[0],
      };
      api
        .postWithFiles('transfer/bulkUpload', requestBody, true, false)
        .then((data: IBulkUploadData) => {
          setMainData(data.bulkUploadData);
          if (data.bulkUploadData?.length > 0) {
            setTableData(data.bulkUploadData);
            setTotalRows(data.bulkUploadData.length);
            const allValid = data.bulkUploadData.some((item) => item.code === 10);
            if (allValid) {
              setDisableMakeTransfer(true);
            } else {
              setDisableMakeTransfer(false);
            }
          }
        })
        .catch((error: IApiResponse) => {
          console.error(error);
          setSimpleWarning(error.description || 'Bulk upload failed.');
          setSubmitStatus(AxiosStatus.FAILURE);
          closeConfirmation();
        })
        .finally(() => {
          setSubmitStatus(AxiosStatus.DEFAULT);
        });
      // setTimeout(() => {
      //   Object.keys(values).forEach((key) => {
      //     form.mutators.setFieldTouched(`${key}`, false);
      //   });
      //   form.reset();
      // }, 0);
    },
    [accounts, extras],
  );

  const onConfirm = useCallback(
    (values: IFormValues) => (otp: string, saveBeneficiary: boolean) => {
      setErrorMsg('');
      setSubmitStatus(AxiosStatus.PIN_PENDING);

      const translist = mainData.map((item) => ({
        transactionAmount: item.transactionAmount,
        transactionType: item.transactionType,
        transactionNarration: item.transactionNarration,
        beneficiaryAccount: item.beneficiaryAccount,
        beneficiaryName: item.beneficiaryName,
        beneficiaryBank: item.beneficiaryBank,
        transactionDescription: item.transactionDescription,
        bankCode: item.transactionType === 'instant' ? item?.beneficiaryBankCode : item.bankBranch,
        charge: item.charge ? item.charge : 0,
        beneficiaryId: item.beneficiaryId,
      }));

      return validateOtpPin(otp)
        .then(() => {
          setSubmitStatus(AxiosStatus.PENDING);
          const { sourceAccount } = values;
          const fromAccount = accounts.find((a) => a.accountNumber === sourceAccount);
          const requestBody = {
            sourceAccount: values.sourceAccount,
            sourceAccountType: fromAccount?.accountType,
            transferData: JSON.stringify(translist),
            password: otp,
            passwordType: 'OTP',
            saveBen: saveBeneficiary,
            isSchedule: !!values.scheduleTransfer,
            transferType: 'TRANSFER',
          };

          api
            .post('transfer/manuelBulkTransfer', requestBody)
            .then((response: ITransferResponse) => {
              setSuccessMsg(response.description || 'Transfer Completed Successfully!');
              closeConfirmation();
              setSubmitStatus(AxiosStatus.SUCCESS);
            })
            .catch((error: IApiResponse) => {
              console.error(error);
              setErrorMsg(error.description || 'Transfer failed.\nUnable to complete the transaction.');
              setSubmitStatus(AxiosStatus.FAILURE);
              closeConfirmation();
            });
        })
        .catch((error: IOTPError) => {
          setOTPError(error);
          handlePinError(setSubmitStatus, setErrorMsg);
        });
    },
    [accounts, mainData],
  );

  const handleDeleteFile = (e: React.MouseEvent<HTMLDivElement, MouseEvent>, form: FormType) => {
    e.stopPropagation();
    form.change('files', []);
    mainForm.change('files', []);
    setTableData([]);
  };

  useEffect(() => {
    const data = mainData;
    const indexOfLastPost = pageNumber * pageSize;
    const indexOfFirstPost = indexOfLastPost - pageSize;
    const currentData = data.slice(indexOfFirstPost, indexOfLastPost);
    setTableData(currentData);
  }, [mainData, pageNumber, pageSize]);

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPageNumber(newPage);
  };

  const handlePageSizeChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const newPageSize = parseInt(event.target.value as string, 10);
    setPageSize(newPageSize);
    setPageNumber(1);
  };

  const handleCancelBulk = () => {
    setTableData([]);
    mainForm.change('files', []);
  };

  const subTitleItem = (
    <div>
      <p className={styles.subTitleItem}>
        Download the template below, complete it and upload to the section on the right
      </p>
      <a href="/excel_templates/Bulk_Transfer_template.xlsx" target="_parent" download>
        <Button type="button" className={styles.btnSubmit} theme={ButtonThemes.secondary} size={ButtonSizes.small}>
          <img src={download} alt="delete icon" />
          <span className={styles.downloadText}>Download Template</span>
        </Button>
      </a>
    </div>
  );

  const renderTableData = () => (
    <>
      <Table columns={columns} data={tableData} emptyTableMessage={emptyMessage} />
      <TablePagination
        count={Math.ceil(totalRows / pageSize)}
        page={pageNumber}
        pageSize={pageSize}
        totalCount={totalRows}
        handlePageChange={handlePageChange}
        handlePageSizeChange={handlePageSizeChange}
      />
    </>
  );

  const classes = useStyles();

  const toggleScheduleTransfer = () => setToggleSchedule((prev) => !prev);

  const dateFilterChange = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const handleClearDateInput = (name: string, value?: string | null | undefined) => {
    mainForm.change('startDate', value as string);
  };

  const renderBulkUploadField = (values: IFormValues, form: FormType) => {
    if (Object.keys(values).length !== 0) {
      if (values.files && values.files.length > 0) {
        return (
          <>
            <div className={styles.valueContentContainer} aria-disabled={submitting}>
              <div className={styles.nameAndSizeContainer}>
                <img src={excel} alt="excel icon" />
                <div>
                  <p className={styles.name}>{values.files[0] && values.files[0].name}</p>
                  <p className={styles.size}>{values.files[0] && getFileSize(values.files[0].size)}</p>
                </div>
              </div>
              <div
                className={styles.fileDeleteBtn}
                onClick={(e) => handleDeleteFile(e, form)}
                role="button"
                tabIndex={0}
                onKeyPress={noop}
              >
                <img src={trash} alt="delete icon" width="40" height="40" />
              </div>
            </div>
          </>
        );
      }
    }

    return renderBulkTransferUpload(submitting);
  };

  return (
    <>
      {showMainForm && (
        <DashboardCard>
          <Form
            onSubmit={handleAddBulkTransfer}
            validate={validate}
            mutators={{ changeFieldValue, setFieldTouched }}
            render={({ handleSubmit, values, form, hasValidationErrors }) => {
              mainForm = mainForm || form;
              return (
                <form onSubmit={handleSubmit}>
                  <FieldsLeveler label="Source Account">{renderTransferFieldsFrom(accounts, submitting)}</FieldsLeveler>
                  <FieldsLeveler label="Beneficiary List" subTitle={subTitleItem}>
                    {renderBulkUploadField(values, form)}
                    {renderScheduleControl(toggleScheduleTransfer, toggleSchedule, classes)}
                    {values.scheduleTransfer &&
                      renderScheduleForm(submitting, dateFilterChange, handleClearDateInput, values)}
                  </FieldsLeveler>
                  <SubmitLeveler>
                    <Button type="submit" disabled={hasValidationErrors || submitting} fullWidth>
                      {submitting ? <CircularProgress size={20} color="inherit" /> : 'Upload Form'}
                    </Button>
                    <WarningModal open={!!simpleWarning} onClose={closeSimpleWarning} cancelName="Close">
                      {simpleWarning}
                    </WarningModal>
                  </SubmitLeveler>
                  <OTPConfirmationModal
                    open={isConfirmationOpen}
                    data={previewData}
                    onClose={closeConfirmation}
                    onResend={onResend}
                    OTPPinError={OTPError}
                    onSubmit={onConfirm(values)}
                    submitStatus={submitStatus}
                    onSuccess={handleOnSuccess}
                    onRetry={onRetryConfirm}
                    successText={successMsg}
                    failureText={errorMsg}
                    askSaveBeneficiary
                  />
                  <StatusModal
                    open={showStatusModal}
                    status={submitStatus === AxiosStatus.SUCCESS ? 'success' : 'failure'}
                    onClose={onCloseStatusModal}
                    onRetry={onCloseStatusModal}
                    onRetryText="Close"
                  >
                    {submitStatus === AxiosStatus.SUCCESS ? (
                      successMsg
                    ) : (
                      <div style={{ whiteSpace: 'pre-line' }}>{errorMsg}</div>
                    )}
                  </StatusModal>
                  <FormSpy subscription={{ values: true, dirty: true }} onChange={onChangeForm} />
                </form>
              );
            }}
          />
        </DashboardCard>
      )}
      <DashboardCard>
        {bulkTransfersState === 'adding' && tableData.length > 0 && (
          <>
            <NavigationTitle to="#/" layoutType={LayoutType.dashboard} noArrow>
              Bulk Transfers List
            </NavigationTitle>
            {renderTableData()}
            <SubmitLeveler>
              <Button
                type="button"
                fullWidth
                disabled={tableData.length === 0 || disableBulkTransfer || submitting}
                onClick={BulkTransfersConfirmation}
              >
                Make Transfer here
              </Button>
            </SubmitLeveler>
            <SubmitLeveler>
              <div
                role="button"
                className={styles.cancelTransfer}
                onClick={handleCancelBulk}
                tabIndex={0}
                onKeyPress={noop}
              >
                Cancel Bulk Transfer
              </div>
            </SubmitLeveler>
          </>
        )}
      </DashboardCard>
    </>
  );
};

export default BulkFileUpload;
